import { type SegmentedControlOption } from '@air/component-segmented-control';

import {
  type ShareCustomColors,
  type ShareDesignColorRole,
} from '~/components/ShareModal/components/ShareDesignColorRow';
import {
  DEFAULT_BUTTON_BG_HEX,
  DEFAULT_HEADER_BG_HEX,
  DEFAULT_PAGE_BG_HEX,
  type ShareDesignColorRoleConfig,
} from '~/components/ShareModal/shareLinkDesignResolution';
import { COVER_FADE_DEFAULT } from '~/components/v2/components/showcase/showcaseCoverFade';
import { COVER_WIDTH_DEFAULT } from '~/components/v2/components/showcase/showcaseCoverWidth';

/**
 * COLLECTION-LINK-ONLY design authoring config, shared by the create modal
 * (`CreateShowcaseDesignProvider`) and the editor (`ShowcaseEditorDesignProvider`):
 * the color-role builder those two providers wrap with their namespaced testIds, plus the
 * cover-width/cover-fade controls the board share tab doesn't offer. Everything share-link-generic
 * (brand-context mapping, the hex resolution chain, defaults, the colorMode/thumbnailRadius/
 * pageWidth option arrays) lives in `ShareModal/shareLinkDesignResolution.ts`.
 * Keep this file pure (no React, no hooks).
 */

export const SHOWCASE_DESIGN_COLOR_ROLE_CONFIGS: ShareDesignColorRoleConfig[] = [
  {
    analyticsRole: 'buttons_and_headlines',
    defaultHex: DEFAULT_BUTTON_BG_HEX,
    label: 'Buttons & headlines',
    readKey: 'buttonBgColor',
    // One control authors both stored roles so public buttons and headlines always match.
    writeKeys: ['buttonBgColor', 'headlineBgColor'],
  },
  {
    analyticsRole: 'header_background',
    defaultHex: DEFAULT_HEADER_BG_HEX,
    label: 'Header background',
    readKey: 'headerBgColor',
    writeKeys: ['headerBgColor'],
  },
  {
    analyticsRole: 'page_background',
    defaultHex: DEFAULT_PAGE_BG_HEX,
    label: 'Page background',
    readKey: 'pageBgColor',
    writeKeys: ['pageBgColor'],
  },
];

/**
 * Builds a consumer's color-role list by pairing each shared config with that consumer's namespaced
 * testId, looked up by `readKey` (NOT array position) — so reordering the configs above can never
 * silently mis-pair a testId. Throws if a consumer omits a role's testId, surfacing the mistake at
 * module load rather than as a wrong test selector at runtime.
 */
export const buildShowcaseDesignColorRoles = (
  testIdByReadKey: Partial<Record<keyof ShareCustomColors, string>>,
): ShareDesignColorRole[] =>
  SHOWCASE_DESIGN_COLOR_ROLE_CONFIGS.map((config) => {
    const testId = testIdByReadKey[config.readKey];

    if (!testId) {
      throw new Error(`Missing color-role testId for "${config.readKey}"`);
    }

    return { ...config, testId };
  });

/*
 * Cover width has a backend field (`presentationSettings.coverWidth`) — the option values mirror the
 * wire union verbatim so persisting surfaces need no boundary mapping. The collection link EDITOR
 * commits it to its draft; the CREATE modal still treats it as preview-only local state (wiring the
 * create flow is a separate slice).
 *
 * The default is re-exported from the shared collection folder: absent `coverWidth` must mean the
 * same thing on every surface, and the canonical absent-value fallback lives next to the public
 * renderer (`PublicShowcaseCover`) that legacy links depend on. See `showcaseCoverWidth.ts`.
 */
export { COVER_WIDTH_DEFAULT };
export const SHOWCASE_DESIGN_COVER_WIDTH_OPTIONS: SegmentedControlOption[] = [
  { label: 'Inset', value: 'inset', id: 'inset', tooltip: 'Cover padded in from the edges' },
  { label: 'Full width', value: 'fullWidth', id: 'fullWidth', tooltip: 'Cover spans the full width' },
  {
    label: 'Immersive',
    value: 'immersive',
    id: 'immersive',
    tooltip: 'Cover spans full width and overlaps the header',
  },
];
/**
 * The option object backing COVER_WIDTH_DEFAULT, derived (not indexed) so the providers' default
 * context seeds can never disagree with the value default above. The `?? [0]` arm is unreachable
 * while the options mirror the union; it only guards a future option/default mismatch.
 */
export const DEFAULT_COVER_WIDTH_OPTION =
  SHOWCASE_DESIGN_COVER_WIDTH_OPTIONS.find((option) => option.value === COVER_WIDTH_DEFAULT) ??
  SHOWCASE_DESIGN_COVER_WIDTH_OPTIONS[0];

/*
 * Cover fade has a backend field (`presentationSettings.coverFade`, a top-level boolean scalar). The
 * collection link EDITOR commits it to its draft and the EDIT modal live-saves it via its commit
 * sink; the CREATE modal's toggle is still preview-only local state (wiring the create flow is a
 * separate slice).
 *
 * The default is re-exported from the shared collection folder: absent `coverFade` must mean the
 * same thing on the authoring surfaces as it does in the public renderer's fallback that legacy
 * links depend on. See `showcaseCoverFade.ts`.
 */
export { COVER_FADE_DEFAULT };
